import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useHistory } from 'react-router';
import { AIConversationType } from '../../../../../../extViewmodels/sdk/models/AI';
import { useSelectionState } from '../../../../../../hooks/useSelectionState';
import { AiButton } from '../../../../../../web/components-v2/AiButton';
import { Button } from '../../../../../../web/components-v2/Button';
import { ILeadViewFilter } from '../../../../../entities/LeadView/useLeadView';
import {
	DefaultCallAnalysisFilter,
	DefaultCallAnalysisSort,
	useCallAnalysisSearch,
} from '../../hooks/useCallAnalysisSearch';
import { useMultiCallAnalysis } from '../../hooks/useMultiCallAnalysis';
import { styleSheet } from '../../styles';
import { ANALYSIS_TYPES } from '../../types';
import { CallAnalysisModal } from '../CallAnalysisModal';
import { CallAnalysisTable } from '../CallAnalysisTable';
import { CallFilters } from '../CallFilters';
import { ManageMessagesDialog } from '../ManageMessagesDialog';
import { SavedMessagesDropdown } from '../SavedMessagesDropdown';
import { useSavedMessages } from '../../hooks/useSavedMessages';
import { CoachingTemplateSelector } from '../CoachingTemplateSelector';
import { useSelectedAgentTemplate } from '../../hooks/useSelectedAgentTemplate';

interface LiveCallsTabProps {
	onSDRClick?: (userId: string, userName: string) => void;
}

export const LiveCallsTab: React.FC<LiveCallsTabProps> = ({ onSDRClick }) => {
	const history = useHistory();

	// Agent template selection
	const { selectedTemplateId, setSelectedTemplateId } = useSelectedAgentTemplate();

	// Saved messages state
	const { getMessageById, setLastUsed, lastUsedId } = useSavedMessages();
	const [currentMessageId, setCurrentMessageId] = React.useState<string | null>(null);
	const [isManageDialogOpen, setIsManageDialogOpen] = React.useState(false);
	const [dropdownKey, setDropdownKey] = React.useState(0);

	// Initialize with last used on mount
	React.useEffect(() => {
		if (lastUsedId) {
			setCurrentMessageId(lastUsedId);
		}
	}, [lastUsedId]);

	// URL parameter utilities
	const parseUrlParams = React.useCallback(() => {
		const params = new URLSearchParams(history.location.search);
		const urlFilter = { ...DefaultCallAnalysisFilter };

		const startDate = params.get('startDate');
		const endDate = params.get('endDate');
		if (startDate && endDate) {
			urlFilter.date = {
				start: new Date(startDate),
				end: new Date(endDate),
			};
		}

		const userIds = params.get('userIds');
		const userId = params.get('userId');

		if (userIds) {
			const ids = userIds.split(',').filter(id => id.trim());
			urlFilter.users = ids.map(id => ({ id: id.trim() }));
			if (ids.length > 0) {
				urlFilter.user = { id: ids[0].trim() };
			}
		} else if (userId) {
			urlFilter.user = { id: userId };
		}

		const managerId = params.get('managerId');
		if (managerId) {
			urlFilter.manager = { id: managerId };
		}

		const team = params.get('team');
		if (team) {
			urlFilter.team = team;
		}

		const teams = params.get('teams');
		if (teams) {
			try {
				urlFilter.teams = JSON.parse(teams);
			} catch {
				// Ignore parsing errors
			}
		}

		const companyTag = params.get('companyTag');
		if (companyTag) {
			urlFilter.companyTag = companyTag;
		}

		const outcomes = params.get('outcomes');
		if (outcomes) {
			try {
				urlFilter.outcomes = JSON.parse(outcomes);
			} catch {
				// Ignore parsing errors
			}
		}

		const recordingChannels = params.get('recordingChannels');
		if (recordingChannels) {
			try {
				urlFilter.recordingChannels = JSON.parse(recordingChannels);
			} catch {
				// Ignore parsing errors
			}
		}

		return urlFilter;
	}, [history.location.search]);

	const updateUrlParams = React.useCallback(
		(filterState: ILeadViewFilter) => {
			const params = new URLSearchParams();

			if (filterState.date?.start && filterState.date?.end) {
				params.set('startDate', filterState.date.start.toISOString().split('T')[0]);
				params.set('endDate', filterState.date.end.toISOString().split('T')[0]);
			}

			if (filterState.users && filterState.users.length > 0) {
				params.set('userIds', filterState.users.map(user => user.id).join(','));
			} else if (filterState.user?.id) {
				params.set('userId', filterState.user.id);
			}

			if (filterState.manager?.id) {
				params.set('managerId', filterState.manager.id);
			}

			if (filterState.team) {
				params.set('team', filterState.team);
			}

			if (filterState.teams && filterState.teams.length > 0) {
				params.set('teams', JSON.stringify(filterState.teams));
			}

			if (filterState.companyTag) {
				params.set('companyTag', filterState.companyTag);
			}

			if (filterState.outcomes && filterState.outcomes.length > 0) {
				params.set('outcomes', JSON.stringify(filterState.outcomes));
			}

			if (filterState.recordingChannels) {
				params.set('recordingChannels', filterState.recordingChannels);
			}

			const paramString = params.toString();
			const newUrl = paramString ? `${history.location.pathname}?${paramString}` : history.location.pathname;

			if (newUrl !== history.location.pathname + history.location.search) {
				history.replace(newUrl);
			}
		},
		[history]
	);

	const initialFilter = React.useMemo(() => {
		const urlFilter = parseUrlParams();
		const hasUrlParams = history.location.search.length > 0;
		return hasUrlParams ? urlFilter : DefaultCallAnalysisFilter;
	}, [parseUrlParams, history.location.search]);

	const {
		filter,
		setFilter: setFilterInternal,
		calls,
		sort,
		setSort,
		isLoading: isLoadingCalls,
		loadMore,
		hasNextPage,
		isFetchingNextPage,
	} = useCallAnalysisSearch(initialFilter);

	const selectableCallIds = React.useMemo(
		() => calls.filter(call => call.hasTranscription).map(call => call.phoneCall.id),
		[calls]
	);

	const {
		selectedRows: selectedCallIds,
		excludedRows,
		toggleRow: handleToggleCall,
		toggleSelectAll: handleToggleSelectAll,
		reset: resetSelection,
		isSelected: isCallSelected,
		isAllSelected,
		isPartialSelected,
	} = useSelectionState({ rows: selectableCallIds });

	const setFilter = React.useCallback(
		(newFilter: ILeadViewFilter) => {
			setFilterInternal(newFilter);
			updateUrlParams(newFilter);
		},
		[setFilterInternal, updateUrlParams]
	);

	const [bulkSelectionKey, setBulkSelectionKey] = React.useState(0);

	const selectedCalls = React.useMemo(() => {
		const callsWithTranscription = calls.filter(call => call.hasTranscription);
		if (isAllSelected) {
			return callsWithTranscription.filter(call => !excludedRows.includes(call.phoneCall.id));
		}
		return callsWithTranscription.filter(call => selectedCallIds.includes(call.phoneCall.id));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [calls, selectedCallIds, isAllSelected, excludedRows, bulkSelectionKey]);

	const {
		selectedAnalysisType: multiSelectedAnalysisType,
		isAnalysisModalOpen: isMultiAnalysisModalOpen,
		startMultiAnalysis,
		closeAnalysis: closeMultiAnalysis,
		aiResponse: multiAiResponse,
		isLoading: isLoadingMultiAnalysis,
		conversationMessages: multiConversationMessages,
		conversationId: multiConversationId,
		sendFollowUp: sendMultiFollowUp,
		lastAiResponseCompleted: lastMultiAiResponseCompleted,
		selectedCallCount,
	} = useMultiCallAnalysis({
		selectedCalls,
		companyId: selectedCalls[0]?.company?.id,
		leadServedId: undefined,
		source: 'call_analysis_page',
		agentTemplateId: selectedTemplateId,
	});

	const handleCloseMultiAnalysis = React.useCallback(() => {
		closeMultiAnalysis();
	}, [closeMultiAnalysis]);

	const handleSelectCall = React.useCallback(
		(callId: string, _selected: boolean, isBulk?: boolean) => {
			handleToggleCall(callId, isBulk);
			if (isBulk) {
				setBulkSelectionKey(prev => prev + 1);
			}
		},
		[handleToggleCall]
	);

	const handleSelectAll = React.useCallback(
		(_selected: boolean) => {
			handleToggleSelectAll();
		},
		[handleToggleSelectAll]
	);

	// Get current message content
	const currentMessage = currentMessageId ? getMessageById(currentMessageId) : null;

	// Handler for selecting message
	const handleSelectMessage = React.useCallback(
		(content: string, messageId: string) => {
			setCurrentMessageId(messageId);
			setLastUsed(messageId);
		},
		[setLastUsed]
	);

	const handleAnalyzeMultipleCalls = React.useCallback(
		(analysisTypeKey: string) => {
			const analysisType = ANALYSIS_TYPES.find(type => type.key === analysisTypeKey);
			if (!analysisType || selectedCalls.length === 0) return;

			const customMessage = currentMessage?.content.trim() || undefined;
			startMultiAnalysis(analysisType, customMessage);
		},
		[startMultiAnalysis, selectedCalls.length, currentMessage]
	);

	const handleClearFilters = React.useCallback(() => {
		setFilter(DefaultCallAnalysisFilter);
		setSort(DefaultCallAnalysisSort);
	}, [setFilter, setSort]);

	const handleSort = React.useCallback(
		(newSort: Api.ISortDescriptor) => {
			setSort(newSort);
		},
		[setSort]
	);

	return (
		<div className={css(styleSheet.content)}>
			<CallFilters filter={filter} onFilterChange={setFilter} onClearFilters={handleClearFilters} />

			{/* Bulk analysis controls */}
			<div className={css(styleSheet.bulkActionsContainer)}>
				<div className={css(styleSheet.bulkActionsContent)}>
					<span className={css(styleSheet.selectedCount)}>
						{selectedCalls.length > 0
							? `${selectedCalls.length} call${selectedCalls.length !== 1 ? 's' : ''} selected`
							: 'No calls selected'}
					</span>
					<div className={css(styleSheet.bulkActions)}>
						{/* Agent Template Selector */}
						<div>
							<CoachingTemplateSelector
								value={selectedTemplateId}
								onValueChange={setSelectedTemplateId}
								coachingTypes={[AIConversationType.SalesPostCallCoaching]}
							/>
						</div>
						{/* Custom System Message Dropdown */}
						<div>
							<div className={css(styleSheet.customMessageRow)}>
								<label className={css(styleSheet.customMessageLabel)}>Custom Message:</label>
								<SavedMessagesDropdown
									onSelectMessage={handleSelectMessage}
									onOpenManageDialog={() => setIsManageDialogOpen(true)}
									currentMessageId={currentMessageId}
									refreshKey={dropdownKey}
								/>
								{currentMessageId && (
									<Button variant='ghost' size='sm' onClick={() => setCurrentMessageId(null)}>
										Clear
									</Button>
								)}
							</div>
						</div>
						{ANALYSIS_TYPES.map(analysisType => (
							<AiButton
								key={analysisType.key}
								title={isLoadingMultiAnalysis ? 'Analyzing...' : 'Analyze'}
								onClick={() => handleAnalyzeMultipleCalls(analysisType.key)}
								disabled={isLoadingMultiAnalysis || selectedCalls.length === 0}
								isLoading={isLoadingMultiAnalysis}
								size='sm'
							/>
						))}
						<Button onClick={resetSelection} disabled={selectedCalls.length === 0} size='sm'>
							Clear Selection
						</Button>
					</div>
				</div>
			</div>

			<div className={css(styleSheet.tableContainer)}>
				<CallAnalysisTable
					calls={calls}
					isLoading={isLoadingCalls}
					sort={sort}
					onSort={handleSort}
					onClearFilters={handleClearFilters}
					loadMore={loadMore}
					hasNextPage={hasNextPage}
					isFetchingNextPage={isFetchingNextPage}
					selectedCallIds={selectedCallIds}
					onSelectCall={handleSelectCall}
					onSelectAll={handleSelectAll}
					onAnalyzeMultipleCalls={handleAnalyzeMultipleCalls}
					isAllSelected={isAllSelected}
					isPartialSelected={isPartialSelected}
					isCallSelected={isCallSelected}
					onSDRClick={onSDRClick}
				/>
			</div>

			{/* Multi-call analysis modal */}
			<CallAnalysisModal
				isOpen={isMultiAnalysisModalOpen}
				onClose={handleCloseMultiAnalysis}
				analysisType={multiSelectedAnalysisType}
				aiResponse={multiAiResponse}
				isLoading={isLoadingMultiAnalysis}
				conversationMessages={multiConversationMessages}
				conversationId={multiConversationId}
				onSendFollowUp={sendMultiFollowUp}
				lastAiResponseCompleted={lastMultiAiResponseCompleted}
				isMultiCall={true}
				selectedCallCount={selectedCallCount}
			/>

			{/* Manage Messages Dialog */}
			<ManageMessagesDialog
				isOpen={isManageDialogOpen}
				onClose={() => {
					setIsManageDialogOpen(false);
					setDropdownKey(prev => prev + 1); // Force dropdown to remount and reload
				}}
			/>
		</div>
	);
};
